import {Component, OnInit} from '@angular/core';
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {ConquerVipService} from '../../../core/services/vip/conquer-vip.service';
import {UserBindVip, UserBindVipBinding} from '../../../core/models/vip/user-bind-vip.model';
import {CommonModule} from '@angular/common';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {LoadingComponent} from '../../../shared/components/loading/loading.component';
import {ButtonComponent} from '../../../shared/components/button/button.component';
import {RecaptchaV3Service} from '../../../core/services/recaptcha/recaptcha-v3-service';

@Component({
  selector: 'app-conquer-vip',
  standalone: true,
  imports: [
    TranslateModule,
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    LoadingComponent
  ],
  templateUrl: './conquer-vip.component.html',
  styleUrl: './conquer-vip.component.scss'
})
export class ConquerVipComponent implements OnInit {
  // VIP data
  vipInfo: UserBindVip | null = null;

  // Form
  bindForm: FormGroup;

  // Loading states
  loading: boolean = true;
  bindLoading: boolean = false;
  unbindLoading: boolean = false;

  // Error messages
  bindError: string | null = null;
  unbindError: string | null = null;

  // Modal state
  showUnbindModal: boolean = false;
  selectedBinding: UserBindVipBinding | null = null;

  // VIP rules - for translation support
  vipRules: string[] = [
    'CONQUER_VIP.RULES.RULE_1',
    'CONQUER_VIP.RULES.RULE_2',
    'CONQUER_VIP.RULES.RULE_3',
    'CONQUER_VIP.RULES.RULE_4',
    'CONQUER_VIP.RULES.RULE_5',
    'CONQUER_VIP.RULES.RULE_6'
  ];

  constructor(
    private readonly recaptchaService: RecaptchaV3Service,
    private readonly vipService: ConquerVipService,
    private readonly fb: FormBuilder,
    private readonly translate: TranslateService
  ) {
    this.bindForm = this.fb.group({
      userName: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.loadVipInfo();
  }

  loadVipInfo(): void {
    this.loading = true;

    this.vipService.getVipInfo(true).subscribe({
      next: (response) => {
        this.vipInfo = response;
        this.loading = false;
      },
      error: (err) => {
        console.error(err);
        this.loading = false;
      }
    });
  }

  bindVipAccount(): void {
    if (this.bindForm.invalid) {
      return;
    }

    this.bindLoading = true;
    this.bindError = null;
    const userName = this.bindForm.get('userName')?.value;

    this.recaptchaService.execute('vipBindConquerAccount', (token) => {
      this.vipService.bindVipAccount(token, userName).subscribe({
        next: () => {
          // Reset the form
          this.bindForm.reset();
          this.bindLoading = false;
          this.ngOnInit();
        },
        error: (err) => {
          this.bindError = err.message || this.translate.instant('CONQUER_VIP.ERRORS.BIND_FAILED');
          this.bindLoading = false;
          this.ngOnInit();
        }
      });
    });
  }

  openUnbindModal(binding: UserBindVipBinding): void {
    this.selectedBinding = binding;
    this.showUnbindModal = true;
  }

  closeUnbindModal(): void {
    this.showUnbindModal = false;
    this.selectedBinding = null;
    this.unbindError = null;
  }

  unbindVipAccount(): void {
    if (!this.selectedBinding) {
      return;
    }

    this.unbindLoading = true;
    this.unbindError = null;

    this.recaptchaService.execute('vipUnbindConquerAccount', (token) => {
      if (!this.selectedBinding) {
        this.unbindLoading = false;
        return;
      }

      this.vipService.unbindVipAccount(token, this.selectedBinding.conquerAccountId).subscribe({
        next: () => {
          this.unbindLoading = false;
          this.closeUnbindModal();
          this.ngOnInit();
        },
        error: (err) => {
          this.unbindError = err.message || this.translate.instant('CONQUER_VIP.ERRORS.UNBIND_FAILED');
          this.unbindLoading = false;
          this.ngOnInit();
        }
      });
    });
  }
}
